package com.siriusxm.pia.views.unifiedaggregator.types

import com.siriusxm.smithy4kt.SmithyShape
import com.siriusxm.smithy4kt.SmithyValidatorException
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive

/**
 * Recursively removes null values from a JSON tree. Smithy unions require exactly one
 * member to be set, but API responses often include null for inactive members (e.g.,
 * `"fixed": null`). Stripping nulls prevents false "union has more than one field" errors.
 */
private fun JsonElement.stripNullValues(): JsonElement = when (this) {
    is JsonObject -> JsonObject(
        entries
            .filter { (_, v) -> v !is JsonNull }
            .associate { (k, v) -> k to v.stripNullValues() }
    )
    is JsonArray -> JsonArray(map { it.stripNullValues() })
    else -> this
}

private const val ENTITY_ID_SHAPE_ID = "contentingestion.unifiedmodel#entityId"
private const val ENTITY_ID_MIN_LENGTH = 4

private fun shouldValidateShape(shape: Any?, element: JsonElement): Boolean {
    val candidateId = (shape as? SmithyShape)?.id ?: return true
    if (candidateId != ENTITY_ID_SHAPE_ID) {
        return true
    }

    val primitive = element as? JsonPrimitive ?: return true
    if (!primitive.isString) {
        return true
    }

    val entityId = primitive.content
    // Skip the Smithy validator for entity IDs that are strings with a reasonable length.
    // This preserves legacy behavior where non-UUID identifiers (e.g., "XCP:4081") are allowed.
    return entityId.length < ENTITY_ID_MIN_LENGTH
}

internal fun validateAgainstSmithy(
    rawInput: String,
    shape: SmithyShape,
    successMessage: String,
    expectedType: String? = null,
    expectedVersion: String? = null,
    idPattern: String? = null
): ValidationResult {
    if (rawInput.isBlank()) {
        return ValidationResult.Error(
            "JSON payload is required",
            listOf(ValidationIssue("Paste the JSON you want to validate.")),
            ValidationMetadata()
        )
    }

    val parsedInput = try {
        smithyValidationJson.parseToJsonElement(rawInput)
    } catch (t: SerializationException) {
        return ValidationResult.Error(
            "Invalid JSON",
            listOf(
                ValidationIssue(
                    t.message ?: "Unable to parse the provided JSON string."
                )
            ),
            ValidationMetadata()
        )
    } catch (t: Throwable) {
        return ValidationResult.Error(
            "Invalid JSON",
            listOf(
                ValidationIssue(
                    t.message ?: "Unable to parse the provided JSON string."
                )
            ),
            ValidationMetadata()
        )
    }

    val payload = parsedInput.unwrapValidationPayload()

    // REMOVE events only require id, type, and version — skip full schema validation
    if (payload.metadata.contentUpdateType == "REMOVE") {
        return validateRemovePayload(payload, expectedType, expectedVersion, idPattern)
    }

    val sanitizedTarget = payload.target.stripNullValues()

    return try {
        shape.validate(sanitizedTarget, true) { candidateShape, element ->
            shouldValidateShape(candidateShape, element)
        }

        // Check type and version fields against the entity type configuration
        val metadataIssues = checkEntityMetadata(
            payload.target, expectedType, expectedVersion, payload.metadata.validatedPointer
        )
        val idIssues = checkIdPattern(payload.target, idPattern, payload.metadata.validatedPointer)
        val allIssues = metadataIssues + idIssues

        if (allIssues.isNotEmpty()) {
            ValidationResult.Error(
                "The JSON doesn’t match this entity’s schema",
                allIssues,
                payload.metadata
            )
        } else {
            ValidationResult.Success(
                successMessage,
                payload.metadata
            )
        }
    } catch (t: Throwable) {
        val issues = collectSmithyIssues(t).map { issue ->
            issue.withPointerPrefix(payload.metadata.validatedPointer)
        }
        ValidationResult.Error(
            "The JSON doesn’t match this entity’s schema",
            issues,
            payload.metadata
        )
    }
}

/**
 * Validates a REMOVE content update payload, which only requires id, type, and version fields.
 * Also checks that the type and version values match the expected entity type configuration.
 */
private fun validateRemovePayload(
    payload: ValidationPayload,
    expectedType: String? = null,
    expectedVersion: String? = null,
    idPattern: String? = null
): ValidationResult {
    val obj = payload.target as? JsonObject ?: return ValidationResult.Error(
        "Invalid REMOVE payload",
        listOf(ValidationIssue("REMOVE entity must be a JSON object.")),
        payload.metadata
    )

    // Treat null values as absent — a field set to JSON null is just as invalid as a missing field.
    val missingFields = listOf("id", "type", "version").filter { field ->
        obj[field].let { it == null || it is JsonNull }
    }
    if (missingFields.isNotEmpty()) {
        return ValidationResult.Error(
            "Invalid REMOVE payload",
            missingFields.map { field ->
                ValidationIssue(
                    message = "Required field \"$field\" is missing.",
                    pointer = "$.${field}"
                )
            },
            payload.metadata
        )
    }

    val metadataIssues = checkEntityMetadata(
        payload.target, expectedType, expectedVersion, payload.metadata.validatedPointer
    )
    val idIssues = checkIdPattern(payload.target, idPattern, payload.metadata.validatedPointer)
    val allIssues = metadataIssues + idIssues
    return if (allIssues.isNotEmpty()) {
        ValidationResult.Error(
            "Invalid REMOVE payload",
            allIssues,
            payload.metadata
        )
    } else {
        ValidationResult.Success(
            "Valid REMOVE payload — contains required id, type, and version fields.",
            payload.metadata
        )
    }
}

private data class ValidationPayload(
    val target: JsonElement,
    val metadata: ValidationMetadata
)

private val CONTENT_UPDATE_TYPES = setOf("ADD", "UPDATE", "REMOVE")

private fun JsonElement.unwrapValidationPayload(): ValidationPayload {
    val obj = this as? JsonObject ?: return ValidationPayload(this, ValidationMetadata())

    // Check for CloudEvent envelope first
    val envelope = obj.takeIf { it.looksLikeCloudEventEnvelope() }
    if (envelope != null) {
        val dataElement = envelope["data"]
            ?: return ValidationPayload(this, ValidationMetadata())
        // CloudEvent data may itself be a content update wrapper
        val innerPayload = dataElement.unwrapContentUpdate()
        return if (innerPayload != null) {
            ValidationPayload(
                target = innerPayload.target,
                metadata = innerPayload.metadata.copy(
                    validatedPointer = combinePointers("$.data", innerPayload.metadata.validatedPointer),
                    isCloudEventEnvelope = true
                )
            )
        } else {
            ValidationPayload(
                target = dataElement,
                metadata = ValidationMetadata(
                    validatedPointer = "$.data",
                    isCloudEventEnvelope = true
                )
            )
        }
    }

    // Check for content update wrapper (e.g., { "type": "REMOVE", "entity": {...} })
    val contentUpdate = obj.unwrapContentUpdate()
    if (contentUpdate != null) {
        return contentUpdate
    }

    return ValidationPayload(this, ValidationMetadata())
}

/**
 * Detects content update wrappers with a type field (ADD/UPDATE/REMOVE) and extracts
 * the inner entity or modification payload for validation.
 */
private fun JsonElement.unwrapContentUpdate(): ValidationPayload? {
    val obj = this as? JsonObject ?: return null
    val updateType = obj["type"]?.jsonPrimitive?.contentOrNull?.uppercase() ?: return null
    if (updateType !in CONTENT_UPDATE_TYPES) return null

    val entityField = obj["entity"] ?: obj["modification"] ?: return null
    val fieldName = if (obj.containsKey("entity")) "entity" else "modification"

    return ValidationPayload(
        target = entityField,
        metadata = ValidationMetadata(
            validatedPointer = "$.${fieldName}",
            isContentUpdateEnvelope = true,
            contentUpdateType = updateType
        )
    )
}

private fun JsonObject.looksLikeCloudEventEnvelope(): Boolean {
    if (!this.containsKey("data")) {
        return false
    }
    val specVersion = this["specversion"]?.jsonPrimitive?.contentOrNull
    val eventType = this["type"]?.jsonPrimitive?.contentOrNull
    val eventId = this["id"]?.jsonPrimitive?.contentOrNull
    val eventSource = this["source"]?.jsonPrimitive?.contentOrNull
    val envelopeSignals = listOf(specVersion, eventType, eventId, eventSource).count { !it.isNullOrBlank() }
    return envelopeSignals >= 2
}

private fun ValidationIssue.withPointerPrefix(prefix: String?): ValidationIssue {
    if (prefix.isNullOrBlank()) {
        return this
    }
    val mergedPointer = combinePointers(prefix, pointer)
    return copy(pointer = mergedPointer)
}

private fun combinePointers(prefix: String, pointer: String?): String {
    val normalizedPrefix = prefix.normalizePointerBody()
    val normalizedPointer = pointer.normalizePointerBody()

    return when {
        normalizedPrefix.isEmpty() && normalizedPointer.isEmpty() -> "$"
        normalizedPrefix.isEmpty() -> "$.${normalizedPointer}"
        normalizedPointer.isEmpty() -> "$.${normalizedPrefix}"
        else -> "$.${normalizedPrefix}.${normalizedPointer}"
    }.replace("$..", "$.")
}

private fun String?.normalizePointerBody(): String {
    if (this.isNullOrBlank()) {
        return ""
    }
    return this.removePrefix("$")
        .trimStart('.')
}

private fun collectSmithyIssues(error: Throwable): List<ValidationIssue> {
    val visited = mutableSetOf<Throwable>()
    val issues = mutableListOf<ValidationIssue>()
    var current: Throwable? = error
    while (current != null && visited.add(current)) {
        formatSmithyIssue(current)?.let { issue ->
            issues.addAll(issue.expand())
        }
        current = current.cause
    }
    return issues.distinct().ifEmpty {
        listOf(ValidationIssue("Validation failed without additional error details."))
    }
}

private fun formatSmithyIssue(error: Throwable): ValidationIssue? {
    val rawMessage = error.message?.trim().orEmpty()
    if (error is SmithyValidatorException) {
        val memberName = error.member?.takeIf { it.isNotBlank() }
        val pointer = memberName?.let { "$.${it}" }
        val cleanedMessage = if (memberName != null) {
            rawMessage.replace("[object Object]", memberName)
        } else {
            rawMessage
        }
        if (cleanedMessage.isBlank()) {
            return null
        }
        val contextSuffix = error.parentShapeId?.substringAfter('#') ?: error.shapeId?.substringAfter('#')
        return ValidationIssue(
            message = cleanedMessage,
            pointer = pointer,
            schemaContext = contextSuffix
        )
    }

    val normalized = rawMessage
        .split('\n', ';')
        .map { it.trim() }
        .firstOrNull { it.isNotEmpty() }

    return normalized?.let { ValidationIssue(it) }
}

private fun ValidationIssue.expand(): List<ValidationIssue> {
    extractExtraStructureMembers()?.let { return it }

    val parts = message
        .split('\n', ';')
        .map { it.trim() }
        .filter { it.isNotEmpty() }
    if (parts.isEmpty()) {
        return emptyList()
    }
    if (parts.size == 1) {
        return listOf(this.copy(message = parts.first()))
    }
    return parts.map { part -> this.copy(message = part) }
}

private fun ValidationIssue.extractExtraStructureMembers(): List<ValidationIssue>? {
    val prefix = "Extra structure members found:"
    if (!message.startsWith(prefix)) {
        return null
    }
    val members = message.substringAfter(prefix)
        .split(',')
        .map { it.trim().trim('"') }
        .filter { it.isNotEmpty() }
    if (members.isEmpty()) {
        return null
    }

    val pointerPrefix = pointer?.takeIf { it.isNotBlank() } ?: "$."
    return members.map { member ->
        val memberPointer = if (pointerPrefix == "$.") {
            "$.${member}"
        } else {
            combinePointers(pointerPrefix, "$.${member}")
        }
        copy(
            message = "Extra structure member \"${member}\" is not allowed.",
            pointer = memberPointer
        )
    }
}

data class ValidationIssue(
    val message: String,
    val pointer: String? = null,
    val schemaContext: String? = null
)

data class ValidationMetadata(
    val validatedPointer: String? = null,
    val isCloudEventEnvelope: Boolean = false,
    val isContentUpdateEnvelope: Boolean = false,
    val contentUpdateType: String? = null
)

sealed interface ValidationResult {
    val metadata: ValidationMetadata

    data object Idle : ValidationResult {
        override val metadata: ValidationMetadata = ValidationMetadata()
    }

    data class Success(
        val message: String,
        override val metadata: ValidationMetadata
    ) : ValidationResult

    data class Error(
        val title: String,
        val issues: List<ValidationIssue>,
        override val metadata: ValidationMetadata
    ) : ValidationResult
}

private fun checkEntityMetadata(
    target: JsonElement,
    expectedType: String?,
    expectedVersion: String?,
    pointerPrefix: String?
): List<ValidationIssue> {
    val obj = target as? JsonObject ?: return emptyList()
    val issues = mutableListOf<ValidationIssue>()
    val prefix = pointerPrefix ?: "$"

    if (expectedType != null) {
        val typeField = obj["type"]
        val actualType = (typeField as? JsonPrimitive)?.contentOrNull
        if (typeField != null && typeField !is JsonPrimitive) {
            issues.add(
                ValidationIssue(
                    message = "Type must be a primitive value.",
                    pointer = combinePointers(prefix, "$.type")
                )
            )
        } else if (typeField is JsonNull) {
            issues.add(
                ValidationIssue(
                    message = "Type must not be null.",
                    pointer = combinePointers(prefix, "$.type")
                )
            )
        } else if (actualType != null && actualType != expectedType) {
            issues.add(
                ValidationIssue(
                    message = "Type \"$actualType\" does not match the expected type \"$expectedType\".",
                    pointer = combinePointers(prefix, "$.type")
                )
            )
        }
    }

    if (expectedVersion != null) {
        val versionField = obj["version"]
        val actualVersion = (versionField as? JsonPrimitive)?.contentOrNull
        if (versionField != null && versionField !is JsonPrimitive) {
            issues.add(
                ValidationIssue(
                    message = "Version must be a primitive value.",
                    pointer = combinePointers(prefix, "$.version")
                )
            )
        } else if (versionField is JsonNull) {
            issues.add(
                ValidationIssue(
                    message = "Version must not be null.",
                    pointer = combinePointers(prefix, "$.version")
                )
            )
        } else if (actualVersion != null && actualVersion != expectedVersion) {
            issues.add(
                ValidationIssue(
                    message = "Version \"$actualVersion\" does not match the expected version \"$expectedVersion\" for this entity type.",
                    pointer = combinePointers(prefix, "$.version")
                )
            )
        }
    }

    return issues
}

private fun checkIdPattern(
    target: JsonElement,
    idPattern: String?,
    pointerPrefix: String?
): List<ValidationIssue> {
    if (idPattern.isNullOrBlank()) return emptyList()
    val obj = target as? JsonObject ?: return emptyList()
    val idField = obj["id"]
    val actualId = (idField as? JsonPrimitive)?.contentOrNull ?: return emptyList()
    val prefix = pointerPrefix ?: "$"

    val regex = try {
        Regex(idPattern)
    } catch (_: Throwable) {
        // A misconfigured idPattern is a configuration problem, not a valid id. Surface it
        // rather than silently skipping id validation, which would let any id through.
        return listOf(
            ValidationIssue(
                message = "Entity type has an invalid id pattern and could not be checked: $idPattern",
                pointer = combinePointers(prefix, "$.id")
            )
        )
    }

    if (!regex.matches(actualId)) {
        val message = friendlyIdPatternMessage(actualId, idPattern)
        return listOf(
            ValidationIssue(
                message = message,
                pointer = combinePointers(prefix, "$.id")
            )
        )
    }
    return emptyList()
}

private fun friendlyIdPatternMessage(actualId: String, idPattern: String): String {
    val literalPrefix = idPrefixFromPattern(idPattern)
    if (literalPrefix != null) {
        return "Entity ID must start with \"$literalPrefix\" — got \"$actualId\"."
    }
    return "Entity ID \"$actualId\" does not match the required pattern: $idPattern"
}

/**
 * Extracts the literal prefix an entity id must start with, from an anchored [pattern] such as
 * `^RE:[0-9a-f]{8}-…`. Returns the leading run of letters/digits plus `:` and `-` when it contains
 * a `:` (the convention for typed-entity id prefixes like `RE:` or `TB:`), otherwise null.
 *
 * Shared by the example generator (to build a matching id) and the validator's friendly error
 * message, so the two can never disagree on what the prefix is.
 */
internal fun idPrefixFromPattern(pattern: String): String? {
    val normalized = pattern.removePrefix("^")
    val literalPrefix = normalized.takeWhile { it.isLetterOrDigit() || it == ':' || it == '-' }
    return literalPrefix.takeIf { it.isNotEmpty() && it.contains(':') }
}

internal val smithyValidationJson = Json {
    ignoreUnknownKeys = false
    isLenient = false
    coerceInputValues = false
}
